define((require) => {
    "use strict";

    var Backbone                 = require("backbone"),
        EditorEvents             = require("shared/services/EditorEvents"),
        ImprovedModel            = require("shared/zbone/ImprovedModel"),
        ImprovedView             = require("editor/zbone/ImprovedView"),
        Ladda                    = require("ladda"),
        Login                    = require("security/Login"),
        PasswordQualityValidator = require("util/PasswordQualityValidator"),
        PasswordQualityView      = require("ui/user/PasswordQualityView"),
        {isBlank}                = require("shared/util/StringUtils"),
        template                 = require("ui/user/RegistrationView.tpl"),
        Tooltip                  = require("ui/common/Tooltip");

    /**
     * The view model for the registration view.
     *
     * <p>This model assists in binding form elements that aren't present on the actual user model (password confirm,
     * keep signed in) and handles validation of inputs.</p>
     *
     * @class RegistrationViewModel
     * @constructor
     */
    var RegistrationViewModel = ImprovedModel.extend({
        defaults: {
            name: "",
            email: "",
            password: "",
            passwordConfirm: "",
            keepSignedIn: true
        },

        computed: {
            nameValid: {
                deps: ["name"],
                value:  function(name) {
                    return this._validateName(name);
                }
            },
            passwordValid: {
                deps: ["password"],
                value: function(password) {
                    return this._validatePassword(password);
                }
            },
            passwordConfirmValid: {
                deps: ["passwordConfirm"],
                value: function(passwordConfirm) {
                    return this._validatePassword(passwordConfirm);
                }
            },
            passwordsMatch: {
                deps: ["password", "passwordConfirm"],
                value: function(password, passwordConfirm) {
                    return this._passwordsMatch(password, passwordConfirm);
                }
            },
            valid: {
                deps: ["name", "password", "passwordConfirm"],
                value: function(email, password, passwordConfirm) {
                    return this._validateName(email) && this._validatePassword(password) && this._validatePassword(passwordConfirm) && this._passwordsMatch(password, passwordConfirm);
                }
            }
        },

        _validateName: function(name) {
            return !isBlank(name);
        },

        _validatePassword: function(password) {
            return PasswordQualityValidator.isValid(password);
        },

        _passwordsMatch: function(password, passwordConfirm) {
            return password === passwordConfirm;
        }
    });

    /**
     * The registration overlay view, shown when a new user is being registered.
     *
     * @author Bruce Mackenzie Nielsen
     * @class RegistrationView
     * @constructor
     */
    return ImprovedView.extend({

        _viewModel: null,
        _progressIndicator: null,

        template: template,
        className: "registration-overlay",

        viewModelBindings: {
            ".name-input": "name",
            ".email-input": "email",
            ".password-input": "password",
            ".retype-password-input": "passwordConfirm",
            ".persistent-input": "keepSignedIn"
        },

        ui: {
            name: ".name-input",
            password: ".password-input",
            passwordConfirm: ".retype-password-input",
            logInButton: "button"
        },

        events: {
            "submit form": function(event) {
                // Stop form from submitting normally
                event.preventDefault();

                // Update the display of validation errors and break out if the form is invalid
                this._updateValidationDisplay(true);
                if (!this._viewModel.get("valid")) {
                    return false;
                }

                var eventProperties = {
                    username: this.model.get("username"),
                    isNewUser: true,
                    triggerOrigin: "RegistrationLogInButton"
                };

                EditorEvents.trigger(EditorEvents.constants.ACCESS_USER, eventProperties);

                // Start showing the progress indicator
                this._progressIndicator.start();

                // Update user model with new name (before set password to ensure username exist for any mail actions when password is set)
                this.model.set({
                    name: this._viewModel.get("name")
                });

                var self = this;

                this.model.save().then(() => {
                    // Change the password
                    return Login.changePassword(self._viewModel.get("password"), self._viewModel.get("keepSignedIn"));
                })
                    .done(() => {
                    // Close the view
                        self.remove();
                        // Navigate to the dashboard only if this is not coming from a share action. If if comes from a share option we already have the experience in the background.
                        if (!self.options.isShare) {
                            Backbone.history.navigate("", true);
                        }
                    })
                    .always(() => {
                        self._progressIndicator.stop();
                    });
                return false;
            }
        },

        /**
         * Updates the display of validation errors on the form.
         *
         * @param {boolean} formSubmit Whether the form is being submitted.
         * @private
         */
        _updateValidationDisplay: function(formSubmit) {
            // Name field
            if (this._viewModel.get("nameValid")) {
                this.ui.name.parent().removeClass("validation-error");
            } else if (formSubmit) {
                this.ui.name.parent().addClass("validation-error");
            }

            // Password field
            this.ui.password.attr("placeholder", "Choose a password");
            if (this._viewModel.get("passwordValid")) {
                this.ui.password.parent().removeClass("validation-error");
                this.ui.passwordConfirm.parent().removeClass("validation-error");
            } else if (formSubmit) {
                this.ui.password.parent().addClass("validation-error");
            }

            // Password confirm field
            this.ui.passwordConfirm.attr("placeholder", "Retype password");
            if (this._viewModel.get("passwordConfirmValid")) {
                this.ui.password.parent().removeClass("validation-error");
                this.ui.passwordConfirm.parent().removeClass("validation-error");
            } else if (formSubmit) {
                this.ui.passwordConfirm.parent().addClass("validation-error");
            }

            // Password mismatch (only considered on form submit, and if passwords are otherwise valid)
            if (formSubmit && !this._viewModel.get("passwordsMatch") &&
                this._viewModel.get("passwordValid") && this._viewModel.get("passwordConfirmValid")) {
                this.ui.password.parent().addClass("validation-error");
                this.ui.password.attr("placeholder", "Password mismatch");
                this.ui.password.val("");
                this.ui.passwordConfirm.parent().addClass("validation-error");
                this.ui.passwordConfirm.attr("placeholder", "Password mismatch");
                this.ui.passwordConfirm.val("");
                this._viewModel.set({
                    password: "",
                    passwordValid: false,
                    passwordConfirm: "",
                    passwordConfirmValid: false
                });
            }

            // Form valid
            if (this._viewModel.get("valid")) {
                this.ui.logInButton.addClass("valid");
                this.ui.logInButton.removeClass("validation-error");
            } else {
                this.ui.logInButton.removeClass("valid");
                if (formSubmit) {
                    this.ui.logInButton.addClass("validation-error");
                }
            }
        },

        onRender: function() {
            // Bind the form to a registration view model
            this._viewModel = new RegistrationViewModel({
                name: this.model.get("name"),
                email: this.model.get("username")
            });
            this.stickit(this._viewModel, this.viewModelBindings);

            // When a field changes state to valid, ensure the validation error class is removed
            this.listenTo(this._viewModel, "change:nameValid change:passwordValid change:passwordConfirmValid change:valid", function() {
                this._updateValidationDisplay(false);
            });

            // Create the "Ladda" instance - https://github.com/hakimel/Ladda
            this._progressIndicator = Ladda.create(this.ui.logInButton.get(0));

            // Add tooltip for password quality
            Tooltip.add(this.ui.password, {
                width: 230,
                height: 210,
                placement: "right",
                trigger: "focus",
                offset: {align: "right"},
                delay: {hide: 0},
                html: true,
                duration: -1,
                classes: "password-quality-tooltip",
                content: new PasswordQualityView({model: this._viewModel})
            });

            this.ui.password.focus();

            var eventProperties = {
                username: this.model.get("username"),
                triggerOrigin: "RegistrationEmailLink"
            };

            EditorEvents.trigger(EditorEvents.constants.OPEN_REGISTRATION_PAGE, eventProperties);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/user/RegistrationView.js